export type QuoteStatus = 'Draft' | 'Internal Review' | 'Approved Internal' | 'Rejected' | 'Changes Requested' | 'Sent' | 'Viewed' | 'Accepted' | 'Declined' | 'Expired' | 'Withdrawn' | 'Converted';
export type LineType = 'Test' | 'Labor' | 'Subheading' | 'Freeform';

/**
 * BUG-2026-0083 — Statuses where a quote is NOT yet approved internally and
 * therefore cannot be added to the schedule. The minimum required state is
 * "Approved Internal" (i.e. the quote has passed the first internal review).
 *
 * Draft / Internal Review → not yet approved.
 * Rejected / Changes Requested → approval explicitly failed (or required edits).
 *
 * Used by QuoteEditorPage to gate the "Add to Schedule" button and (on the
 * backend) by scheduleTaskGenerationService to refuse task generation.
 */
export const QUOTE_STATUSES_BLOCKING_SCHEDULE: readonly QuoteStatus[] = [
  'Draft',
  'Internal Review',
  'Rejected',
  'Changes Requested',
] as const;

export function isQuoteScheduleable(status: QuoteStatus, quoteType?: string): boolean {
  // REQ-01: internal quotes are schedule-only and bypass the approval gate, so
  // they can be added to the schedule from any status. External quotes keep the
  // BUG-2026-0083 internal-review gate.
  if (quoteType === 'Internal') return true;
  return !QUOTE_STATUSES_BLOCKING_SCHEDULE.includes(status);
}

export interface Quote {
  QuoteKey: string;
  QuoteNumber: string;
  Revision: number;
  Status: QuoteStatus;
  QuoteType: 'Internal' | 'External';
  ClientKey: string | null;
  Title: string | null;
  Currency: string;
  /**
   * Snapshotted FX rate at quote creation and re-locked at Sent time.
   * 1.0 when Currency === 'CAD'. Null when no rate period was configured
   * at the time of creation (admin must configure a rate period).
   * Locked (read-only) once Status >= Sent.
   */
  FxRateAtQuote: number | null;
  IssueDate: string | null;
  ValidUntil: string | null;
  ScopeOfWork: string | null;
  Assumptions: string | null;
  Terms: string | null;
  IntroMessage: string | null;
  DiscountType: string | null;
  DiscountValue: number | null;
  PmFeePercent: number | null;
  TaxType: string | null;
  TaxPercent: number | null;
  Subtotal: number;
  DiscountAmount: number;
  PmFeeAmount: number;
  TaxAmount: number;
  GrandTotal: number;
  PreparedByUserId: string | null;
}

export interface QuoteWithClient extends Quote {
  ClientName: string | null;
  PreparedByName: string | null;
}

export interface QuoteLineItem {
  QuoteLineItemKey: string;
  QuoteKey: string;
  LineNumber: number;
  LineType: LineType;
  TestCatalogItemKey: string | null;
  AstmCode: string | null;
  Description: string | null;
  LabNote: string | null;
  Quantity: number;
  Unit: string | null;
  UnitPrice: number;
  LineDiscountPercent: number;
  LineTotal: number;
  LaborHours: number | null;
  LaborRate: number | null;
  IsOptional: boolean;
  IsHiddenOnPdf: boolean;
}

export interface QuoteDetail extends QuoteWithClient {
  lineItems: QuoteLineItem[];
}

export interface CreateQuoteRequest {
  QuoteType?: 'Internal' | 'External';
  ClientKey?: string;
  Title?: string;
  Currency?: string;
  IssueDate?: string;
  ValidUntil?: string;
  PmFeePercent?: number;
  TaxType?: string;
  TaxPercent?: number;
}

export interface CreateQuoteLineItemRequest {
  LineType: LineType;
  TestCatalogItemKey?: string;
  AstmCode?: string;
  Description?: string;
  LabNote?: string;
  Quantity?: number;
  Unit?: string;
  UnitPrice?: number;
  LineDiscountPercent?: number;
  LaborHours?: number;
  LaborRate?: number;
  IsOptional?: boolean;
  IsHiddenOnPdf?: boolean;
}

export interface QuoteEvent {
  QuoteEventKey: string;
  QuoteKey: string;
  EventType: string;
  UserId: string;
  Details: string | null;
  Timestamp: string;
}
